import { Suspense, useCallback, useState } from "react";
import { SidebarSimple } from "@phosphor-icons/react";
import { NavLink, Outlet } from "react-router-dom";

import { useAuth, useIsAdminFor } from "../auth/AuthProvider";
import { useEnv } from "../env/EnvProvider";
import { EnvSwitcher } from "./EnvSwitcher";
import { RouteFallback } from "./RouteFallback";

interface NavItem {
  to: string;
  label: string;
}

const NAV: NavItem[] = [
  { to: "/creators", label: "Creators" },
  { to: "/moderation", label: "Moderation" },
  { to: "/user-text", label: "User Text" },
  { to: "/feedback", label: "Feedback" },
  { to: "/recipes", label: "Recipes" },
  { to: "/cook-sessions", label: "Cook Sessions" },
  { to: "/catalog", label: "Catalog" },
  { to: "/skills", label: "Skills" },
  { to: "/product-links", label: "Product Links" },
  { to: "/make-it-links", label: "Make-it links" },
  { to: "/nutrition", label: "Nutrition" },
  { to: "/users", label: "Users" },
  { to: "/beta-signups", label: "Beta Signups" },
  { to: "/events", label: "Events" },
  { to: "/promo-offers", label: "Promo Offers" },
  { to: "/announcements", label: "Announcements" },
  { to: "/notifications", label: "Notifications" },
  { to: "/internal-tools", label: "Internal Tools" },
  { to: "/failed-imports", label: "Failed Imports" },
  { to: "/analytics", label: "Analytics" },
  { to: "/ops", label: "Ops & Cost" },
  { to: "/jobs", label: "Jobs" },
  { to: "/settings", label: "Settings" },
];

// The nav can be hidden to give wide screens like Verify's three panes the
// room on a small display (#2913). Remembered per browser; storage can be
// unavailable (private window), so it falls back to shown.
const NAV_COLLAPSED_KEY = "saltpig-admin.nav-collapsed";

function readNavCollapsed(): boolean {
  try {
    return window.localStorage.getItem(NAV_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

function useNavCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(readNavCollapsed);
  const toggle = useCallback(() => {
    setCollapsed((c) => {
      try {
        window.localStorage.setItem(NAV_COLLAPSED_KEY, c ? "0" : "1");
      } catch {
        // Not remembered; the toggle still works for this page.
      }
      return !c;
    });
  }, []);
  return [collapsed, toggle];
}

function ProdBanner() {
  const env = useEnv();
  const isProdAdmin = useIsAdminFor("prod");
  if (env.name !== "prod") return null;
  if (isProdAdmin) {
    return (
      <div
        role="status"
        className="border-b border-red-700 bg-red-600 px-4 py-1 text-center text-xs font-semibold uppercase tracking-wider text-white"
      >
        Production environment — mutations require typed PROD confirmation
      </div>
    );
  }
  return (
    <div
      role="status"
      title="You do not hold the admin:prod role. Mutating actions will be rejected by the API."
      className="flex items-center justify-center gap-2 border-b border-bg-card-elevated bg-bg-card-elevated px-4 py-1 text-center text-xs text-text-muted"
    >
      <span aria-hidden>{"\u{1F512}"}</span>
      <span>Read-only — admin:prod role not granted</span>
    </div>
  );
}

export function AppShell() {
  const { user, signOut } = useAuth();
  const userLabel =
    user?.profile?.preferred_username ??
    user?.profile?.email ??
    user?.profile?.name ??
    "Signed in";
  const [navCollapsed, toggleNav] = useNavCollapsed();
  return (
    <div className="flex h-full">
      <aside
        className={`${navCollapsed ? "hidden" : "flex"} w-56 shrink-0 flex-col border-r border-bg-card-elevated bg-bg-card`}
      >
        <div className="px-4 py-5">
          <div className="text-base font-semibold text-text-light">saltpig Admin</div>
          <div className="mt-1 text-[11px] uppercase tracking-wide text-text-muted">
            Internal
          </div>
        </div>
        <nav className="flex-1 px-2">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                [
                  "block rounded px-3 py-2 text-sm",
                  isActive
                    ? "bg-bg-card-elevated text-text-light"
                    : "text-text-muted hover:bg-bg-card-elevated hover:text-text-light",
                ].join(" ")
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <main className="flex min-w-0 flex-1 flex-col">
        <ProdBanner />
        <header className="flex items-center justify-between border-b border-bg-card-elevated bg-bg-card px-4 py-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleNav}
              aria-expanded={!navCollapsed}
              aria-label={navCollapsed ? "Show navigation" : "Hide navigation"}
              title={navCollapsed ? "Show navigation" : "Hide navigation"}
              className="rounded p-1 text-text-muted hover:bg-bg-card-elevated hover:text-text-light"
            >
              <SidebarSimple size={18} />
            </button>
            <div className="text-sm text-text-muted">saltpig admin</div>
          </div>
          <div className="flex items-center gap-3">
            <EnvSwitcher />
            <span className="text-xs text-text-muted">{userLabel}</span>
            <button
              type="button"
              onClick={() => {
                void signOut();
              }}
              className="rounded bg-bg-card-elevated px-2 py-1 text-xs text-text-light hover:opacity-90"
              title="Sign out"
            >
              Sign out
            </button>
          </div>
        </header>
        <section className="min-h-0 flex-1 overflow-auto p-6">
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </section>
      </main>
    </div>
  );
}
